import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Row, Col } from 'antd';
import {
    HomeOutlined,
    ThunderboltOutlined,
    SwapOutlined,
    SettingOutlined,
    CarOutlined,
    LinkOutlined,
    DisconnectOutlined,
    PhoneOutlined,
    CustomerServiceOutlined,
    CheckCircleOutlined,
    ArrowLeftOutlined,
    ArrowRightOutlined,
    AppleOutlined
} from '@ant-design/icons';
import { useScrollAnimation } from '../../common/hooks';
import { FEATURES, GOOGLE_PLAY_URL, APP_STORE_URL, APP_STORE_AVAILABLE } from '../../common/constants';
import './FeatureDetailPage.css';

// Icon mapping
const iconMap = {
    'thunderbolt': ThunderboltOutlined,
    'swap': SwapOutlined,
    'setting': SettingOutlined,
    'car': CarOutlined,
    'link': LinkOutlined,
    'disconnect': DisconnectOutlined,
    'phone': PhoneOutlined,
    'customer-service': CustomerServiceOutlined,
};

// Color gradients for icons
const colorGradients = {
    '#fa8c16': 'linear-gradient(135deg, #fa8c16 0%, #ffc53d 100%)',
    '#52c41a': 'linear-gradient(135deg, #52c41a 0%, #95de64 100%)',
    '#1890ff': 'linear-gradient(135deg, #1890ff 0%, #69c0ff 100%)',
    '#13c2c2': 'linear-gradient(135deg, #13c2c2 0%, #5cdbd3 100%)',
    '#722ed1': 'linear-gradient(135deg, #722ed1 0%, #b37feb 100%)',
    '#eb2f96': 'linear-gradient(135deg, #eb2f96 0%, #ff85c0 100%)',
    '#2f54eb': 'linear-gradient(135deg, #2f54eb 0%, #85a5ff 100%)',
    '#faad14': 'linear-gradient(135deg, #D4A853 0%, #f5d89a 100%)',
};

const FeatureDetailPage = () => {
    const { featureKey } = useParams();
    const navigate = useNavigate();
    const [pageLoaded, setPageLoaded] = useState(false);

    // Find the feature data
    const feature = FEATURES.find(f => f.key === featureKey);
    const featureIndex = FEATURES.findIndex(f => f.key === featureKey);
    const prevFeature = featureIndex > 0 ? FEATURES[featureIndex - 1] : null;
    const nextFeature = featureIndex < FEATURES.length - 1 ? FEATURES[featureIndex + 1] : null;

    // Animation hooks
    const [introRef, introVisible] = useScrollAnimation({ threshold: 0.1 });
    const [benefitsRef, benefitsVisible] = useScrollAnimation({ threshold: 0.1 });
    const [stepsRef, stepsVisible] = useScrollAnimation({ threshold: 0.1 });
    const [ctaRef, ctaVisible] = useScrollAnimation({ threshold: 0.1 });

    useEffect(() => {
        setTimeout(() => setPageLoaded(true), 100);
        window.scrollTo(0, 0);
    }, [featureKey]);

    // If feature not found, redirect to features page
    if (!feature) {
        return (
            <div className="feature-not-found">
                <h2>Fonctionnalité non trouvée</h2>
                <p>La fonctionnalité que vous recherchez n'existe pas.</p>
                <Link to="/features" className="back-link">
                    <ArrowLeftOutlined /> Retour aux fonctionnalités
                </Link>
            </div>
        );
    }

    const IconComponent = iconMap[feature.icon] || ThunderboltOutlined;
    const gradient = colorGradients[feature.color] || colorGradients['#fa8c16'];

    return (
        <div className="feature-detail-page">
            {/* Hero Banner */}
            <section className={`feature-hero ${pageLoaded ? 'banner-loaded' : ''}`}>
                <div className="hero-bg-pattern"></div>
                <div className="container">
                    <nav className={`breadcrumb ${pageLoaded ? 'animate-in' : ''}`}>
                        <Link to="/"><HomeOutlined /> Accueil</Link>
                        <span className="separator">/</span>
                        <Link to="/features">Fonctionnalités</Link>
                        <span className="separator">/</span>
                        <span className="current">{feature.title}</span>
                    </nav>
                    <div className={`hero-content ${pageLoaded ? 'animate-in' : ''}`}>
                        <div
                            className="hero-icon"
                            style={{ background: gradient }}
                        >
                            <IconComponent />
                        </div>
                        <h1>{feature.title}</h1>
                        <p className="hero-description">{feature.description}</p>
                    </div>
                </div>
            </section>

            {/* Introduction Section */}
            <section
                ref={introRef}
                className={`intro-section ${introVisible ? 'section-visible' : ''}`}
            >
                <div className="container">
                    <div className={`intro-content ${introVisible ? 'animate-fade-up is-visible' : 'animate-fade-up'}`}>
                        <h2>À propos de cette <span className="text-gold">fonctionnalité</span></h2>
                        <p className="full-description">{feature.fullDescription}</p>
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section
                ref={benefitsRef}
                className={`benefits-section ${benefitsVisible ? 'section-visible' : ''}`}
            >
                <div className="container">
                    <div className={`section-header ${benefitsVisible ? 'animate-fade-down is-visible' : 'animate-fade-down'}`}>
                        <span className="section-badge">Avantages</span>
                        <h2>Pourquoi utiliser <span className="text-gold">{feature.title}</span> ?</h2>
                    </div>
                    <Row gutter={[24, 24]}>
                        {feature.benefits.map((benefit, index) => (
                            <Col xs={24} sm={12} md={8} key={index}>
                                <div
                                    className={`benefit-card ${benefitsVisible ? 'animate-scale is-visible' : 'animate-scale'}`}
                                    style={{ transitionDelay: `${index * 0.1}s` }}
                                >
                                    <div className="benefit-icon" style={{ color: feature.color }}>
                                        <CheckCircleOutlined />
                                    </div>
                                    <p>{benefit}</p>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </div>
            </section>

            {/* How It Works Section */}
            <section
                ref={stepsRef}
                className={`how-it-works-section ${stepsVisible ? 'section-visible' : ''}`}
            >
                <div className="container">
                    <div className={`section-header ${stepsVisible ? 'animate-fade-down is-visible' : 'animate-fade-down'}`}>
                        <span className="section-badge">Processus</span>
                        <h2>Comment <span className="text-gold">ça marche</span> ?</h2>
                    </div>
                    <div className="steps-timeline">
                        {feature.howItWorks.map((step, index) => (
                            <div
                                key={index}
                                className={`timeline-step ${stepsVisible ? 'animate-fade-up is-visible' : 'animate-fade-up'}`}
                                style={{ transitionDelay: `${index * 0.15}s` }}
                            >
                                <div className="step-number" style={{ background: gradient }}>
                                    {step.step}
                                </div>
                                <div className="step-content">
                                    <h3>{step.title}</h3>
                                    <p>{step.description}</p>
                                </div>
                                {index < feature.howItWorks.length - 1 && (
                                    <div className="step-connector"></div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Navigation Between Features */}
            <section className="feature-navigation">
                <div className="container">
                    <div className="nav-links">
                        {prevFeature ? (
                            <Link to={`/features/${prevFeature.key}`} className="nav-link prev">
                                <ArrowLeftOutlined />
                                <div className="nav-text">
                                    <span className="nav-label">Précédent</span>
                                    <span className="nav-title">{prevFeature.title}</span>
                                </div>
                            </Link>
                        ) : (
                            <div className="nav-link empty"></div>
                        )}

                        <Link to="/features" className="nav-link center">
                            Toutes les fonctionnalités
                        </Link>

                        {nextFeature ? (
                            <Link to={`/features/${nextFeature.key}`} className="nav-link next">
                                <div className="nav-text">
                                    <span className="nav-label">Suivant</span>
                                    <span className="nav-title">{nextFeature.title}</span>
                                </div>
                                <ArrowRightOutlined />
                            </Link>
                        ) : (
                            <div className="nav-link empty"></div>
                        )}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section
                ref={ctaRef}
                className={`cta-section ${ctaVisible ? 'section-visible' : ''}`}
            >
                <div className="container">
                    <div className={`cta-content ${ctaVisible ? 'animate-scale is-visible' : 'animate-scale'}`}>
                        <h2>Profitez de <span className="text-gold">{feature.title}</span></h2>
                        <p>Téléchargez TIGOON Connect et accédez à toutes nos fonctionnalités</p>
                        <div className="cta-buttons">
                            <a href={GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer" className="store-badge">
                                <img
                                    src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                                    alt="Google Play"
                                    height="50"
                                />
                            </a>
                            {APP_STORE_AVAILABLE ? (
                                <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="store-badge">
                                    <img
                                        src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg"
                                        alt="App Store"
                                        height="50"
                                    />
                                </a>
                            ) : (
                                <div className="store-badge-coming-soon">
                                    <AppleOutlined />
                                    <span>Bientôt disponible</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default FeatureDetailPage;
